import { useMemo, useState } from "react";
import { Home } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, StatCard, InfoRow, compactINR, inr } from "@/components/tools/kit";
import { GrowthArea, SplitDonut } from "@/components/tools/charts";
import { ExportButtons } from "@/components/tools/ExportButtons";
import { homeLoan } from "@/lib/calculators";

export default function HomeLoan() {
  const [principal, setPrincipal] = useState(8000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const [prepayMonthly, setPrepay] = useState(0);
  const r = useMemo(() => homeLoan({ principal, rate, years, prepayMonthly }), [principal, rate, years, prepayMonthly]);
  const chart = r.yearly.map((y) => ({ year: y.year, balance: y.balance }));

  const buildPayload = () => ({
    title: "My Finanz Planner Home Loan Report",
    subtitle: `${inr(principal)} at ${rate}% for ${years} years`,
    meta: [
      { label: "Loan amount", value: inr(principal) },
      { label: "Interest rate", value: `${rate}% p.a.` },
      { label: "Tenure", value: `${years} years` },
      { label: "Monthly EMI", value: inr(r.emi) },
      { label: "Total interest", value: inr(r.totalInterest) },
      { label: "Total payment", value: inr(r.totalPayment) },
      ...(prepayMonthly > 0 ? [{ label: "Interest saved (prepay)", value: inr(r.interestSaved) }, { label: "Months saved", value: r.monthsSaved }] : []),
    ],
    sheets: [{ name: "Amortization (yearly)", headers: ["Year", "Principal Paid", "Interest Paid", "Balance"], rows: r.yearly.map((y) => [y.year, y.principal, y.interest, y.balance]) }],
  });

  return (
    <ToolLayout title="Home Loan Calculator" subtitle="EMI, amortization, prepayment savings & tax benefit" icon={Home} accent="#D96B43">
      <TwoCol
        left={
          <SectionCard title="Loan details">
            <div className="space-y-5">
              <Field label="Loan amount"><MoneyInput testid="hl-principal" value={principal} onChange={setPrincipal} helper={compactINR(principal)} /></Field>
              <Field label="Interest rate (p.a.)" hint={rate + "%"}><SliderRow testid="hl-rate" value={rate} min={6} max={14} step={0.05} suffix="%" onChange={setRate} /></Field>
              <Field label="Tenure" hint={years + " yrs"}><SliderRow testid="hl-years" value={years} min={1} max={30} suffix=" yr" onChange={setYears} /></Field>
              <Field label="Extra monthly prepayment"><MoneyInput testid="hl-prepay" value={prepayMonthly} onChange={setPrepay} helper={prepayMonthly > 0 ? `Saves ${compactINR(r.interestSaved)} & ${Math.round(r.monthsSaved / 12 * 10) / 10} yrs` : "Optional"} /></Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="hl-emi" label="Monthly EMI" value={compactINR(r.emi)} accent="#D96B43" big />
              <StatCard testid="hl-interest" label="Total Interest" value={compactINR(r.totalInterest)} accent="#D99B26" />
              <StatCard testid="hl-total" label="Total Payment" value={compactINR(r.totalPayment)} accent="#2D6A4F" />
            </div>
            <SectionCard title="Outstanding balance over time"><GrowthArea data={chart} keys={[{ key: "balance", name: "Balance", color: "#D96B43" }]} /></SectionCard>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SectionCard title="Principal vs Interest"><SplitDonut segments={[{ name: "Principal", value: Math.round(principal), color: "#2A6F7F" }, { name: "Interest", value: Math.round(r.totalInterest), color: "#D96B43" }]} /></SectionCard>
              <SectionCard title="First-year tax benefit">
                <InfoRow label="Interest (Sec 24b, cap ₹2L)" value={inr(r.taxBenefitYr)} strong />
                <InfoRow label="Principal (Sec 80C, cap ₹1.5L)" value={inr(Math.min(150000, r.yearly[0]?.principal || 0))} />
                <p className="text-xs text-[#8A9790] mt-3">Old-regime deductions. Actual benefit depends on your tax slab.</p>
              </SectionCard>
            </div>
            <ExportButtons buildPayload={buildPayload} />
          </>
        }
      />
    </ToolLayout>
  );
}
